import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, Trophy, CheckCircle2 } from 'lucide-react';
import { EliminationMatch } from '@/hooks/useLeagueEliminationMatches';
import { useLanguage } from '@/contexts/LanguageContext';

interface EliminationMatchCardProps {
  match: EliminationMatch;
  sport?: string;
}

const EliminationMatchCard: React.FC<EliminationMatchCardProps> = ({ match, sport }) => {
  const { t, language } = useLanguage();

  const phaseLabels: Record<string, string> = {
    'octavos': t('league.roundOf16'),
    'cuartos': t('league.quarterFinals'),
    'semifinal': t('league.semiFinals'),
    'final': t('league.final')
  };

  const translateCourtName = (name: string): string => {
    if (language === 'en') return name.replace(/^Cancha\b/i, 'Court');
    return name;
  };

  const team1Display = match.team1 || t('league.notAssigned');
  const team2Display = match.team2 || t('league.notAssigned');
  
  const getStatusBadge = () => {
    if (match.isCompleted) {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-300">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          {t('league.completedStatus')}
        </Badge>
      );
    }
    
    if (match.team1 && match.team2) {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-300">
          <Calendar className="h-3 w-3 mr-1" />
          {t('league.scheduledStatus')}
        </Badge>
      );
    }
    
    return (
      <Badge className="bg-gray-100 text-gray-700 border-gray-300">
        {t('league.toSchedule')}
      </Badge>
    );
  };

  const getSportIcon = () => {
    switch (sport?.toLowerCase()) {
      case 'tennis':
      case 'tenis':
        return '🎾';
      case 'padel':
      case 'pádel':
        return '🎾';
      case 'pickleball':
        return '🏓';
      default:
        return '🎾';
    }
  };

  return (
    <div className={`bg-card rounded-lg border p-4 space-y-3 ${match.isUserMatch ? 'ring-2 ring-primary border-primary/30' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-yellow-500" />
          <span className="text-sm font-medium text-muted-foreground">
            {phaseLabels[match.phase] || match.phase} {match.matchNumber}
          </span>
          {match.isUserMatch && (
            <Badge className="bg-primary/10 text-primary border-primary/30 text-xs">
              {t('league.yourMatch')}
            </Badge>
          )}
        </div>
        {getStatusBadge()}
      </div>
      
      <div className="text-center space-y-2 py-2">
        <p className={`font-semibold ${match.team1 ? 'text-primary' : 'text-muted-foreground italic'}`}>
          {team1Display}
        </p>
        <div className="flex items-center justify-center gap-2">
          <div className="h-px w-8 bg-border"></div>
          <span className="text-sm text-muted-foreground font-medium">vs</span>
          <div className="h-px w-8 bg-border"></div>
        </div>
        <p className={`font-semibold ${match.team2 ? 'text-green-600' : 'text-muted-foreground italic'}`}>
          {team2Display}
        </p>
      </div>

      {match.isCompleted && match.score && (
        <div className="text-center">
          <Badge className="bg-green-50 text-green-700 border-green-200 text-lg font-bold px-4 py-1">
            {match.score}
          </Badge>
        </div>
      )}
      
      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground pt-2 border-t">
        {match.scheduledDate && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {match.scheduledDate}
          </span>
        )}
        {match.scheduledTime && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {match.scheduledTime}
          </span>
        )}
        {match.court && (
          <span className="flex items-center gap-1">
            <span>{getSportIcon()}</span>
            {translateCourtName(match.court)}
          </span>
        )}
      </div>
    </div>
  );
};

export default EliminationMatchCard;
